import { Link } from 'react-router-dom';

import config from '@/config';
import { NextButton, PrevButton } from '@/shared-components/buttons/components/SliderControls';
import FHSlider from '@/shared-components/sliders/components/FHSlider';
import { formatArticleReadingTime } from '@/utils/articleReadingTime';
import { buildPublicContentPath } from '@/utils/contentRoutes';

import './styles/ArticleSlider.css';

function buildArticleLink(article = {}) {
  return article._id ? buildPublicContentPath({ ...article, post_type: 'article' }, 'article') : '#';
}

function isRecentlyPublished(article = {}) {
  const createdAt = article.createdAt ? new Date(article.createdAt) : null;
  const sevenDaysMs = 7 * 24 * 60 * 60 * 1000;

  return Boolean(createdAt && !Number.isNaN(createdAt.getTime()) && Date.now() - createdAt.getTime() <= sevenDaysMs);
}

function ArticleSlide({ article }) {
  const articleLink = buildArticleLink(article);
  const coverImage = article.coverImageId
    ? `${config.app.imageServiceDomain}/${article.coverImageId}/w=1200`
    : null;
  const headline = article.title || 'Untitled article';

  return (
    <article className="article-slider__slide">
      <Link to={articleLink} state={{ from: true }} className="article-slider__article-card">
        <div className="article-slider__article-media">
          {coverImage ? (
            <img src={coverImage} alt={headline} loading="lazy" />
          ) : (
            <div className="article-slider__article-media-placeholder" aria-hidden="true" />
          )}
          {isRecentlyPublished(article) && (
            <span className="article-slider__article-badge">Recently published</span>
          )}
        </div>

        <div className="article-slider__article-content">
          <h3 className="article-slider__article-title">{headline}</h3>
          {article.description && (
            <p className="article-slider__article-summary">{article.description}</p>
          )}
          <span className="article-slider__article-meta">{formatArticleReadingTime(article)}</span>
        </div>
      </Link>
    </article>
  );
}

export default function ArticleSlider({
  articles = [],
  category = '',
  image,
  link,
}) {
  const HeroTag = link ? Link : 'div';
  const heroImage = image
    ? `${config.app.imageServiceDomain}/${image}/w=1600`
    : null;

  return (
    <section className="article-slider">
      <div className="article-slider__layout">
        <HeroTag
          className={`article-slider__hero ${link ? 'article-slider__hero--link' : ''}`}
          {...(link ? { to: link } : {})}
        >
          {heroImage && <img className="article-slider__hero-image" src={heroImage} alt="" />}
          <div className="article-slider__hero-overlay" aria-hidden="true" />
          <div className="article-slider__hero-content">
            <h2 className="article-slider__heading">{category}</h2>
          </div>
        </HeroTag>

        <div className="article-slider__rail">
          <FHSlider
            customPrevButton={<PrevButton aria-label="Previous article" />}
            customNextButton={<NextButton aria-label="Next article" />}
            className="article-slider__carousel"
            aria-label={`${category} carousel`}
          >
            {articles.map(article => (
              <ArticleSlide key={article._id} article={article} />
            ))}
          </FHSlider>
        </div>
      </div>
    </section>
  );
}
